import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Package, Truck, CreditCard, Clock, RotateCcw, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';

import { PageHeader, Card, Badge, Spinner, type BadgeTone } from '@terra/ui';

import { api } from '../../lib/api';

const DELIVERY_TONE: Record<string, BadgeTone> = {
  PENDING_PAYMENT: 'warning',
  PLACED: 'neutral',
  PARTIALLY_FULFILLED: 'warning',
  COMPLETED: 'success',
  CANCELLED: 'danger',
};

const formatPaise = (p: number) =>
  `₹${(p / 100).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

export function OrderDetailsPage() {
  const { id } = useParams<{ id: string }>();
  const qc = useQueryClient();

  const {
    data: order,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['sales-order-detail', id],
    queryFn: () => api.orders.getOrderGroup(id!),
    enabled: !!id,
  });

  const transitionSub = useMutation({
    mutationFn: ({ subId, status }: { subId: string; status: string }) =>
      api.orders.adminTransitionSubOrder(subId, { status }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['sales-order-detail', id] }),
  });

  /** The last outcome of a return action, shown under that return. */
  const [returnNotice, setReturnNotice] = useState<{
    returnId: string;
    tone: 'ok' | 'error';
    text: string;
  } | null>(null);
  const refresh = () => void qc.invalidateQueries({ queryKey: ['sales-order-detail', id] });
  const failed = (returnId: string) => (err: any) =>
    setReturnNotice({
      returnId,
      tone: 'error',
      text: err?.response?.data?.error?.message ?? err?.message ?? 'That did not work.',
    });

  /** Whose fault each pending return is, as staff will approve it. */
  const [faultChoice, setFaultChoice] = useState<Record<string, 'SELLER' | 'CUSTOMER'>>({});
  const approveReturn = useMutation({
    mutationFn: (returnId: string) => api.orders.approveReturn(returnId, faultChoice[returnId]),
    onSuccess: (res, returnId) => {
      setReturnNotice({
        returnId,
        tone: res.pickupError ? 'error' : 'ok',
        text: res.pickupError
          ? `Approved, but the pickup was not booked: ${res.pickupError}. Retry the pickup, or receive the items by hand.`
          : 'Approved and the reverse pickup is booked.',
      });
      refresh();
    },
    onError: (err, returnId) => failed(returnId)(err),
  });

  const rejectReturn = useMutation({
    mutationFn: ({ returnId, resolution }: { returnId: string; resolution: string }) =>
      api.orders.rejectReturn(returnId, resolution),
    onSuccess: refresh,
    onError: (err, v) => failed(v.returnId)(err),
  });

  const bookPickup = useMutation({
    mutationFn: (returnId: string) => api.orders.createReturnShipment(returnId),
    onSuccess: (_res, returnId) => {
      setReturnNotice({ returnId, tone: 'ok', text: 'Reverse pickup booked.' });
      refresh();
    },
    onError: (err, returnId) => failed(returnId)(err),
  });

  // A return the seller disputed after inspection: side with the customer
  // (refund) or with the seller (reject, no refund).
  const resolveReturn = useMutation({
    mutationFn: (v: { returnId: string; decision: 'REFUND' | 'REJECT'; resolution: string }) =>
      api.orders.resolveReturnDispute(v.returnId, v.decision, v.resolution),
    onSuccess: (_res, v) => {
      setReturnNotice({
        returnId: v.returnId,
        tone: 'ok',
        text:
          v.decision === 'REFUND'
            ? 'Resolved for the customer: refunded, restocked and the seller settlement adjusted.'
            : 'Resolved for the seller: the return is rejected and no refund is issued.',
      });
      refresh();
    },
    onError: (err, v) => failed(v.returnId)(err),
  });

  const receiveReturn = useMutation({
    mutationFn: (returnId: string) => api.orders.receiveReturn(returnId),
    onSuccess: (_res, returnId) => {
      setReturnNotice({
        returnId,
        tone: 'ok',
        text: 'Marked received: the customer is refunded and the stock is back.',
      });
      refresh();
    },
    onError: (err, returnId) => failed(returnId)(err),
  });

  /** The payment a goodwill refund is being issued on. */
  const [refunding, setRefunding] = useState<{
    id: string;
    amountPaise: number;
    refunds?: Array<{ amountPaise: number; status: string }>;
  } | null>(null);
  const [refundError, setRefundError] = useState<string | null>(null);

  const issueRefund = useMutation({
    mutationFn: (body: { paymentId: string; amountPaise: number; reason: string }) =>
      api.payments.adminRefund(body),
    onSuccess: () => {
      setRefunding(null);
      refresh();
    },
    onError: (err: any) =>
      setRefundError(
        err?.response?.data?.error?.message ?? err?.message ?? 'The refund did not go through.',
      ),
  });

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16">
        <Spinner size="lg" />
        <span className="text-ink-muted text-sm font-medium">Fetching order details...</span>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="py-12 text-center font-medium text-red-500">
        Failed to load order details.
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-12">
      <div className="flex items-center gap-4">
        <Link
          to="/orders/all"
          className="bg-surface-subtle text-ink hover:bg-surface-subtle/80 flex h-10 w-10 items-center justify-center rounded-xl transition-colors"
        >
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <PageHeader
          title={`Order #${order.orderNumber}`}
          description={`Placed on ${new Date(order.createdAt).toLocaleString()}`}
        />
        <div className="ml-auto">
          <Badge tone={DELIVERY_TONE[order.status] ?? 'neutral'} className="px-3 py-1 text-sm">
            {order.status.replace(/_/g, ' ')}
          </Badge>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left Column: Summary */}
        <div className="space-y-6 lg:col-span-1">
          <Card className="space-y-4 p-5">
            <h3 className="text-ink border-border/60 border-b pb-2 font-bold">Customer Summary</h3>
            <div className="space-y-2 text-sm">
              <p>
                <span className="text-ink-muted">Name:</span>{' '}
                <span className="text-ink font-medium">{order.customer.name}</span>
              </p>
              <p>
                <span className="text-ink-muted">Email:</span>{' '}
                <span className="text-ink font-medium">{order.customer.email}</span>
              </p>
            </div>

            <h3 className="text-ink border-border/60 border-b pb-2 pt-4 font-bold">Financials</h3>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-ink-muted">Subtotal:</span>
                <span className="font-medium">{formatPaise(order.itemsSubtotalPaise)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink-muted">Tax:</span>
                <span className="font-medium">{formatPaise(order.taxPaise)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-ink-muted">Discount:</span>
                <span className="font-medium text-green-600">
                  -{formatPaise(order.discountPaise)}
                </span>
              </div>
              <div className="border-border/60 text-ink flex justify-between border-t pt-2 text-base font-bold">
                <span>Grand Total:</span>
                <span>{formatPaise(order.grandTotalPaise)}</span>
              </div>
            </div>
          </Card>

          <Card className="space-y-4 p-5">
            <h3 className="text-ink border-border/60 flex items-center gap-2 border-b pb-2 font-bold">
              <CreditCard className="h-4 w-4" /> Payments & Refunds
            </h3>
            {order.payments.length === 0 && (
              <p className="text-ink-muted text-xs">No payments found.</p>
            )}
            {order.payments.map((p) => (
              <div
                key={p.id}
                className="border-border/60 bg-surface-subtle/30 space-y-2 rounded-xl border p-3 text-sm"
              >
                <div className="flex justify-between font-bold">
                  <span>
                    {p.provider} {p.method ? `(${p.method})` : ''}
                  </span>
                  <span className={p.status === 'CAPTURED' ? 'text-green-600' : 'text-amber-600'}>
                    {p.status}
                  </span>
                </div>
                <div className="text-ink-muted flex justify-between">
                  <span>Amount:</span>
                  <span className="text-ink font-medium">{formatPaise(p.amountPaise)}</span>
                </div>
                {p.refunds && p.refunds.length > 0 && (
                  <div className="mt-2 space-y-1">
                    <p className="text-ink-muted text-xs font-semibold uppercase tracking-wider">
                      Refunds
                    </p>
                    {p.refunds.map((r) => (
                      <div
                        key={r.id}
                        className="flex justify-between rounded bg-red-50 p-1.5 text-xs font-medium text-red-700"
                      >
                        <span>{formatPaise(r.amountPaise)}</span>
                        <span>{r.status}</span>
                      </div>
                    ))}
                  </div>
                )}
                {p.provider === 'RAZORPAY' &&
                  (p.status === 'CAPTURED' || p.status === 'PARTIALLY_REFUNDED') && (
                    <button
                      type="button"
                      onClick={() => {
                        setRefundError(null);
                        setRefunding(p);
                      }}
                      className="border-border/60 hover:bg-surface-subtle text-ink mt-2 w-full rounded-lg border bg-white py-1.5 text-xs font-bold transition-colors"
                    >
                      Goodwill refund
                    </button>
                  )}
              </div>
            ))}
          </Card>
        </div>

        {/* Right Column: Sub-Orders Workflow */}
        <div className="space-y-6 lg:col-span-2">
          {order.subOrders.map((sub) => (
            <Card key={sub.id} className="border-border/60 overflow-hidden border p-0 shadow-sm">
              <div className="bg-surface-subtle/50 border-border/60 flex flex-wrap items-center justify-between gap-4 border-b px-5 py-4">
                <div>
                  <h3 className="text-ink flex items-center gap-2 text-base font-bold">
                    <Package className="text-ink-muted h-5 w-5" />
                    {sub.isInHouse ? 'Inhouse Fulfillment' : `Seller: ${sub.vendor.storeName}`}
                  </h3>
                  <p className="text-ink-muted mt-1 text-xs font-medium">
                    Sub-order ID: <span className="font-mono">{sub.id}</span>
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-right">
                    <p className="text-ink-muted text-xs font-bold uppercase tracking-wider">
                      Status
                    </p>
                    <p className="text-ink text-sm font-bold">{sub.status.replace(/_/g, ' ')}</p>
                  </div>
                  <div className="ml-4 text-right">
                    <p className="text-ink-muted text-xs font-bold uppercase tracking-wider">
                      Total
                    </p>
                    <p className="text-ink text-sm font-bold">{formatPaise(sub.totalPaise)}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-6 p-5">
                {/* Line Items */}
                <div>
                  <h4 className="text-ink-muted border-border/60 mb-3 border-b pb-1 text-xs font-bold uppercase tracking-wider">
                    Items
                  </h4>
                  <div className="space-y-2 text-sm">
                    {sub.lineItems.map((li) => (
                      <div key={li.id} className="flex items-center justify-between">
                        <div className="flex-1">
                          <p className="text-ink font-medium">{li.productTitle}</p>
                          <p className="text-ink-muted font-mono text-xs">
                            SKU: {li.sku} | Qty: {li.qty}
                          </p>
                        </div>
                        <div className="text-ink text-right font-bold">
                          {formatPaise(li.lineSubtotalPaise)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Workflow grid */}
                <div className="grid gap-4 sm:grid-cols-2">
                  {/* Timeline */}
                  <div className="border-border/60 bg-surface-subtle/30 rounded-xl border p-4">
                    <h4 className="text-ink-muted mb-3 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider">
                      <Clock className="h-3.5 w-3.5" /> Timeline Events
                    </h4>
                    <div className="before:via-border/60 relative space-y-3 before:absolute before:inset-0 before:ml-2 before:h-full before:w-0.5 before:-translate-x-px before:bg-gradient-to-b before:from-transparent before:to-transparent md:before:mx-auto md:before:translate-x-0">
                      {sub.statusEvents.map((ev) => (
                        <div
                          key={ev.id}
                          className="border-border/40 relative z-10 flex items-center justify-between rounded-lg border bg-white/50 p-2 text-xs"
                        >
                          <div className="text-ink font-medium">
                            {ev.toStatus.replace(/_/g, ' ')}
                          </div>
                          <div className="text-ink-muted">
                            {new Date(ev.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Shipping */}
                  <div className="border-border/60 bg-surface-subtle/30 rounded-xl border p-4">
                    <h4 className="text-ink-muted mb-3 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider">
                      <Truck className="h-3.5 w-3.5" /> Shipment & Tracking
                    </h4>
                    {sub.shipment ? (
                      <div className="space-y-2 text-sm">
                        <p>
                          <span className="text-ink-muted">Status:</span>{' '}
                          <span className="font-bold">{sub.shipment.status}</span>
                        </p>
                        <p>
                          <span className="text-ink-muted">Courier:</span>{' '}
                          {sub.shipment.courierName || 'N/A'}
                        </p>
                        <p>
                          <span className="text-ink-muted">AWB:</span>{' '}
                          <span className="font-mono">{sub.shipment.awbNumber || 'Pending'}</span>
                        </p>
                        <Link
                          to={`/logistics?q=${encodeURIComponent(sub.shipment.awbNumber || order.orderNumber)}`}
                          className="border-border/60 hover:bg-surface-subtle text-ink mt-2 block w-full rounded-lg border bg-white py-1.5 text-center text-xs font-bold transition-colors"
                        >
                          Track in Logistics
                        </Link>
                      </div>
                    ) : (
                      <div className="space-y-1 py-4 text-center">
                        <p className="text-ink-muted text-xs font-medium">No shipment yet.</p>
                        <p className="text-ink-muted text-[11px]">
                          The seller creates it when packing; it then shows up in Logistics.
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Returns */}
                  {sub.returnRequests && sub.returnRequests.length > 0 && (
                    <div className="border-border/60 rounded-xl border bg-amber-50/50 p-4">
                      <h4 className="mb-3 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-amber-700">
                        <RotateCcw className="h-3.5 w-3.5" /> Return Requests
                      </h4>
                      {sub.returnRequests.map((rr) => {
                        const pickup = rr.returnShipment ?? null;
                        const pickupLive = pickup && pickup.status !== 'CANCELLED';
                        const collected =
                          pickup &&
                          ['PICKED_UP', 'IN_TRANSIT', 'OUT_FOR_DELIVERY', 'DELIVERED'].includes(
                            pickup.status,
                          );
                        const titles = new Map(sub.lineItems.map((li) => [li.id, li.productTitle]));
                        const notice = returnNotice?.returnId === rr.id ? returnNotice : null;
                        return (
                          <div
                            key={rr.id}
                            className="space-y-1.5 border-b border-amber-200/60 pb-3 text-sm last:border-0 last:pb-0"
                          >
                            <div className="flex justify-between font-bold text-amber-900">
                              <span>{rr.status.replace(/_/g, ' ')}</span>
                              <span>{new Date(rr.createdAt).toLocaleDateString()}</span>
                            </div>
                            <p className="text-xs text-amber-900">
                              {rr.items
                                .map(
                                  (it) =>
                                    `${titles.get(it.lineItemId) ?? 'Item'}${it.qty > 1 ? ` × ${it.qty}` : ''}`,
                                )
                                .join(', ')}
                            </p>
                            <p className="text-xs text-amber-800/80">Reason: {rr.reason}</p>
                            {rr.autoApproved && (
                              <p className="text-[11px] font-semibold text-sky-700">
                                Auto-approved (low-risk rules)
                              </p>
                            )}
                            {(rr.photoUrls ?? []).length > 0 && (
                              <div>
                                <p className="text-[11px] font-bold text-amber-900">
                                  Customer&apos;s photos
                                </p>
                                <div className="mt-1 flex flex-wrap gap-1.5">
                                  {(rr.photoUrls ?? []).map((u) => (
                                    <a key={u} href={u} target="_blank" rel="noreferrer">
                                      <img
                                        src={u}
                                        alt=""
                                        className="h-14 w-14 rounded-md border border-amber-200 object-cover"
                                      />
                                    </a>
                                  ))}
                                </div>
                              </div>
                            )}
                            {rr.status === 'QC_PENDING' && rr.qcDeadlineAt && (
                              <p className="text-xs text-sky-800">
                                Back with the seller for inspection until{' '}
                                {new Date(rr.qcDeadlineAt).toLocaleString('en-IN', {
                                  day: 'numeric',
                                  month: 'short',
                                  hour: 'numeric',
                                  minute: '2-digit',
                                })}
                                ; refunded automatically if they don&apos;t act.
                              </p>
                            )}
                            {rr.qcNote && (
                              <p className="text-xs text-amber-900">
                                <span className="font-bold">Seller inspection:</span> {rr.qcNote}
                              </p>
                            )}
                            {(rr.qcPhotoUrls ?? []).length > 0 && (
                              <div>
                                <p className="text-[11px] font-bold text-red-800">
                                  Seller&apos;s photos
                                </p>
                                <div className="mt-1 flex flex-wrap gap-1.5">
                                  {(rr.qcPhotoUrls ?? []).map((u) => (
                                    <a key={u} href={u} target="_blank" rel="noreferrer">
                                      <img
                                        src={u}
                                        alt=""
                                        className="h-14 w-14 rounded-md border border-red-200 object-cover"
                                      />
                                    </a>
                                  ))}
                                </div>
                              </div>
                            )}
                            {rr.resolution && (
                              <p className="text-xs text-amber-800/80">
                                Resolution: {rr.resolution}
                              </p>
                            )}
                            {rr.refundPaise != null && (
                              <p className="text-xs font-semibold text-emerald-700">
                                Refund {formatPaise(rr.refundPaise)}
                              </p>
                            )}
                            {pickup && (
                              <p className="text-xs text-amber-900">
                                Pickup: {pickup.status.replace(/_/g, ' ')}
                                {pickup.awbNumber
                                  ? ` · ${pickup.courierName ?? 'Courier'} ${pickup.awbNumber}`
                                  : ''}
                              </p>
                            )}

                            {rr.status === 'REQUESTED' && (
                              <div className="mt-2 flex gap-2">
                                <button
                                  disabled={approveReturn.isPending}
                                  onClick={() => {
                                    if (
                                      window.confirm('Approve this return and book the pickup?')
                                    ) {
                                      approveReturn.mutate(rr.id);
                                    }
                                  }}
                                  className="flex-1 rounded bg-green-600 py-1 text-xs font-bold text-white transition-colors hover:bg-green-700 disabled:opacity-50"
                                >
                                  Approve
                                </button>
                                <button
                                  disabled={rejectReturn.isPending}
                                  onClick={() => {
                                    const reason = window.prompt(
                                      'Reason for rejection? The customer is shown this.',
                                    );
                                    if (reason?.trim()) {
                                      rejectReturn.mutate({ returnId: rr.id, resolution: reason });
                                    }
                                  }}
                                  className="flex-1 rounded bg-red-600 py-1 text-xs font-bold text-white transition-colors hover:bg-red-700 disabled:opacity-50"
                                >
                                  Reject
                                </button>
                              </div>
                            )}

                            {rr.status === 'QC_FAILED' && (
                              <div className="mt-2 space-y-1.5 rounded-lg border border-red-200 bg-red-50 p-2">
                                <p className="text-xs font-bold text-red-800">
                                  Seller disputes this return: compare both sides&apos; photos and
                                  decide.
                                </p>
                                <div className="flex gap-2">
                                  <button
                                    disabled={resolveReturn.isPending}
                                    onClick={() => {
                                      const note = window.prompt(
                                        'Refund the customer? Note for the record (the seller sees it):',
                                        'Customer claim accepted',
                                      );
                                      if (note?.trim()) {
                                        resolveReturn.mutate({
                                          returnId: rr.id,
                                          decision: 'REFUND',
                                          resolution: note.trim(),
                                        });
                                      }
                                    }}
                                    className="flex-1 rounded bg-emerald-600 py-1 text-xs font-bold text-white transition-colors hover:bg-emerald-700 disabled:opacity-50"
                                  >
                                    Refund customer
                                  </button>
                                  <button
                                    disabled={resolveReturn.isPending}
                                    onClick={() => {
                                      const note = window.prompt(
                                        'Reject the return (no refund)? Reason shown to the customer:',
                                      );
                                      if (note?.trim()) {
                                        resolveReturn.mutate({
                                          returnId: rr.id,
                                          decision: 'REJECT',
                                          resolution: note.trim(),
                                        });
                                      }
                                    }}
                                    className="flex-1 rounded bg-red-600 py-1 text-xs font-bold text-white transition-colors hover:bg-red-700 disabled:opacity-50"
                                  >
                                    Reject return
                                  </button>
                                </div>
                              </div>
                            )}

                            {(rr.status === 'APPROVED' ||
                              rr.status === 'PICKED_UP' ||
                              rr.status === 'QC_PENDING') && (
                              <div className="mt-2 flex flex-wrap gap-2">
                                {rr.status === 'APPROVED' &&
                                  (!pickupLive || !pickup?.awbNumber) && (
                                    <button
                                      disabled={bookPickup.isPending}
                                      onClick={() => bookPickup.mutate(rr.id)}
                                      className="flex-1 rounded bg-amber-600 py-1 text-xs font-bold text-white transition-colors hover:bg-amber-700 disabled:opacity-50"
                                    >
                                      {pickup ? 'Retry pickup' : 'Book pickup'}
                                    </button>
                                  )}
                                <button
                                  disabled={receiveReturn.isPending}
                                  onClick={() => {
                                    if (
                                      window.confirm(
                                        'Mark these items as received back? This refunds the customer, restocks the items and adjusts the seller settlement.',
                                      )
                                    ) {
                                      receiveReturn.mutate(rr.id);
                                    }
                                  }}
                                  className="flex-1 rounded bg-emerald-600 py-1 text-xs font-bold text-white transition-colors hover:bg-emerald-700 disabled:opacity-50"
                                >
                                  {rr.status === 'QC_PENDING'
                                    ? 'Refund now (skip inspection)'
                                    : 'Mark received'}
                                </button>
                                {rr.status === 'APPROVED' && !collected && (
                                  <button
                                    disabled={rejectReturn.isPending}
                                    onClick={() => {
                                      const reason = window.prompt(
                                        'Reason for rejection? The pickup is called off and the customer is shown this.',
                                      );
                                      if (reason?.trim()) {
                                        rejectReturn.mutate({
                                          returnId: rr.id,
                                          resolution: reason,
                                        });
                                      }
                                    }}
                                    className="flex-1 rounded bg-red-600 py-1 text-xs font-bold text-white transition-colors hover:bg-red-700 disabled:opacity-50"
                                  >
                                    Reject
                                  </button>
                                )}
                              </div>
                            )}

                            {notice && (
                              <p
                                className={`mt-1 rounded px-2 py-1 text-xs font-semibold ${
                                  notice.tone === 'error'
                                    ? 'bg-red-50 text-red-700'
                                    : 'bg-emerald-50 text-emerald-700'
                                }`}
                              >
                                {notice.text}
                              </p>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Settlement */}
                  <div className="border-border/60 bg-surface-subtle/30 rounded-xl border p-4 sm:col-span-2">
                    <h4 className="text-ink-muted mb-3 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider">
                      <ShieldCheck className="h-3.5 w-3.5" /> Settlement Linkage
                    </h4>
                    {sub.settlement ? (
                      <div className="grid grid-cols-2 gap-4 text-sm md:grid-cols-4">
                        <div>
                          <p className="text-ink-muted text-xs">Status</p>
                          <p className="text-ink font-bold">{sub.settlement.status}</p>
                        </div>
                        <div>
                          <p className="text-ink-muted text-xs">Gross Amount</p>
                          <p className="text-ink font-bold">
                            {formatPaise(sub.settlement.grossPaise)}
                          </p>
                        </div>
                        <div>
                          <p className="text-ink-muted text-xs">Platform Fee</p>
                          <p className="text-ink font-bold">
                            {formatPaise(sub.settlement.commissionPaise)}
                          </p>
                        </div>
                        <div>
                          <p className="text-ink-muted text-xs">Net Payable</p>
                          <p className="font-bold text-green-600">
                            {formatPaise(sub.settlement.netPayablePaise)}
                          </p>
                        </div>
                      </div>
                    ) : (
                      <p className="text-ink-muted py-1 text-xs font-medium">
                        Sub-order is not yet eligible for settlement (Pending refund window or not
                        delivered).
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {refunding && (
        <GoodwillRefundModal
          payment={refunding}
          pending={issueRefund.isPending}
          error={refundError}
          onClose={() => setRefunding(null)}
          onSubmit={(amountPaise, reason) =>
            issueRefund.mutate({ paymentId: refunding.id, amountPaise, reason })
          }
        />
      )}
    </div>
  );
}

/**
 * Money back to the customer that the platform bears — compensation for a late
 * parcel, a price-match, a service failure. It deliberately leaves the order,
 * the stock and the seller's settlement alone: items coming back or cancelled
 * go through Return / Cancel, which refund and adjust the seller together.
 */
function GoodwillRefundModal({
  payment,
  pending,
  error,
  onClose,
  onSubmit,
}: {
  payment: { amountPaise: number; refunds?: Array<{ amountPaise: number; status: string }> };
  pending: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (amountPaise: number, reason: string) => void;
}) {
  const refunded = (payment.refunds ?? []).reduce(
    (sum, r) => sum + (r.status === 'FAILED' ? 0 : r.amountPaise),
    0,
  );
  const remaining = Math.max(0, payment.amountPaise - refunded);
  const [rupees, setRupees] = useState((remaining / 100).toFixed(2));
  const [reason, setReason] = useState('');

  const amountPaise = Math.round(Number(rupees) * 100);
  const amountOk = Number.isFinite(amountPaise) && amountPaise > 0 && amountPaise <= remaining;
  const reasonOk = reason.trim().length >= 2;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div
        role="dialog"
        aria-modal="true"
        className="w-full max-w-md space-y-4 rounded-2xl bg-white p-6 shadow-xl"
      >
        <h3 className="text-ink text-base font-bold">Goodwill refund</h3>
        <p className="rounded-lg bg-amber-50 p-3 text-xs text-amber-900">
          Paid by the platform. The seller&apos;s settlement, the order status and the stock stay as
          they are. For items being returned or cancelled, use Return or Cancel instead.
        </p>
        <p className="text-ink-muted text-xs">
          Refundable on this payment:{' '}
          <span className="text-ink font-bold">{formatPaise(remaining)}</span>
        </p>
        <label className="block space-y-1 text-sm">
          <span className="text-ink font-semibold">Amount (₹)</span>
          <input
            type="number"
            min="0.01"
            step="0.01"
            value={rupees}
            onChange={(e) => setRupees(e.target.value)}
            className="border-border/60 w-full rounded-lg border px-3 py-2"
          />
          {!amountOk && (
            <span className="text-xs text-red-600">
              Enter an amount above ₹0 and up to {formatPaise(remaining)}.
            </span>
          )}
        </label>
        <label className="block space-y-1 text-sm">
          <span className="text-ink font-semibold">Reason (recorded, sent to Razorpay)</span>
          <input
            value={reason}
            maxLength={500}
            onChange={(e) => setReason(e.target.value)}
            placeholder="e.g. Delivered 5 days late"
            className="border-border/60 w-full rounded-lg border px-3 py-2"
          />
        </label>
        {error && <p className="rounded bg-red-50 px-3 py-2 text-xs text-red-700">{error}</p>}
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="border-border/60 rounded-lg border px-4 py-2 text-sm font-bold"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!amountOk || !reasonOk || pending}
            onClick={() => {
              if (window.confirm(`Refund ${formatPaise(amountPaise)} to the customer now?`)) {
                onSubmit(amountPaise, reason.trim());
              }
            }}
            className="bg-ink flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-bold text-white disabled:opacity-50"
          >
            {pending ? <Spinner size="sm" className="text-white" /> : null}
            Refund
          </button>
        </div>
      </div>
    </div>
  );
}
